// VG 2.0 · Phase 10 — Documents + email client.
import { mintAnonSession } from "./vg2AnonAxios";

const API = process.env.REACT_APP_BACKEND_URL;

async function _handle(r) {
  if (!r.ok) {
    let d; try { d = await r.json(); } catch { d = { detail: r.statusText }; }
    const err = new Error(
      (d?.detail?.message) || (typeof d?.detail === "string" ? d.detail : r.statusText)
    );
    err.status = r.status; err.code = d?.detail?.error_code || null;
    // Preserve the raw detail so callers (and the anon-retry wrapper)
    // can inspect the failure reason.
    err.detail = d?.detail;
    throw err;
  }
  return await r.json();
}

function _anonHeaders() {
  try {
    const t = window.localStorage.getItem("vg2_anon_token");
    return t ? { "X-VG2-Anon-Token": t } : {};
  } catch { return {}; }
}

// LAUNCH-C2 — PA dossier/documents auth via JWT Bearer. No X-PA-Id.
// Owner/anon document functions below are untouched.
function _paHeaders() {
  try {
    const token = window.localStorage.getItem("vg2_token");
    return token ? { "Authorization": `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

function _isStaleAnon401(err) {
  if (!err || err.status !== 401) return false;
  const msg = typeof err.detail === "string"
    ? err.detail
    : (err.detail && err.detail.message) || err.message || "";
  return /anonymous session|anon.*token/i.test(msg);
}

/** Fetch wrapper: on stale-anon 401, mint a fresh token and retry once.
 * The retry only kicks in for callers that pass anon headers (i.e.
 * ``buildHeaders`` is not null). PA endpoints (X-PA-Id) are untouched. */
async function _fetchAnonWithRetry(buildRequest) {
  const attempt = async () => {
    const req = buildRequest();
    const resp = await fetch(req.url, req.init);
    return _handle(resp);
  };
  try {
    return await attempt();
  } catch (err) {
    if (!_isStaleAnon401(err)) throw err;
    // Evict + mint a fresh session; retry once.
    try { window.localStorage.removeItem("vg2_anon_token"); } catch {}
    await mintAnonSession(`${API}/api/v2`);
    return await attempt();
  }
}

export function newOperationId(prefix = "doc") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Owner document.
export async function listOwnerDocuments(caseId) {
  return _fetchAnonWithRetry(() => ({
    url: `${API}/api/v2/cases/${caseId}/documents`,
    init: { method: "GET", headers: _anonHeaders() },
  }));
}

export async function generateOwnerDocument(caseId, operationId) {
  return _fetchAnonWithRetry(() => ({
    url: `${API}/api/v2/cases/${caseId}/documents/action-plan`,
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json", ..._anonHeaders() },
      body: JSON.stringify({ operation_id: operationId }),
    },
  }));
}

export function ownerDocumentDownloadUrl(caseId, documentId) {
  return `${API}/api/v2/cases/${caseId}/documents/${documentId}/pdf`;
}

// PA dossier.
export async function listPaDocuments(paId, caseId) {
  return _handle(await fetch(
    `${API}/api/v2/pa/cases/${caseId}/documents`,
    { method: "GET", headers: _paHeaders(paId) },
  ));
}

export async function generatePaDossier(paId, caseId, operationId) {
  return _handle(await fetch(
    `${API}/api/v2/pa/cases/${caseId}/documents/dossier`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ..._paHeaders(paId) },
      body: JSON.stringify({ operation_id: operationId }),
    },
  ));
}

// Emails.
export async function listCaseEmails(caseId) {
  return _fetchAnonWithRetry(() => ({
    url: `${API}/api/v2/emails/cases/${caseId}`,
    init: { method: "GET", headers: _anonHeaders() },
  }));
}

export async function sendStageEmail(caseId, templateId, operationId) {
  return _fetchAnonWithRetry(() => ({
    url: `${API}/api/v2/emails/send`,
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json", ..._anonHeaders() },
      body: JSON.stringify({
        case_id: caseId, template_id: templateId, operation_id: operationId,
      }),
    },
  }));
}
